import React, { useState, useRef, useEffect, useCallback } from "react";
import { ScrollComponent } from "../../../../_start/assets/ts/components";
import { DataUtil, getCSS } from "../../../../_start/assets/ts/_utils";
import { toAbsoluteUrl } from "../../../../_start/helpers";
import { automaticAnswerMessage, chatExamples, IMessage } from "./ChatData";
import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { functions } from "../../../../config/firebase";

interface ChatCompletionResponse {
  aiResponse: string;
}

interface FirebaseFunctionError {
  code: string;
  message: string;
}

const ChatPage: React.FC = () => {

  const [prompt, setPrompt] = useState('');
  const [output, setOutput] = useState('');
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Handles chat submission via Firebase Cloud Function
   * All AI calls (Google Gemini) are made server-side for security
   */
  const handleSubmit = useCallback(async () => {
    if (!prompt.trim()) {
      setError("Please enter a prompt");
      return;
    }

    const chatCompletion = httpsCallable<{ prompt: string }, ChatCompletionResponse>(functions, 'chatCompletion');

    try {
      setError(null);
      setFetching(true);
      const result = await chatCompletion({ prompt });
      setOutput(result.data.aiResponse);
    } catch (err: unknown) {
      const firebaseError = err as FirebaseFunctionError;
      // Handle Firebase function errors
      if (firebaseError.code === 'functions/unauthenticated') {
        setError("Please log in to use the chat feature.");
      } else if (firebaseError.code === 'functions/resource-exhausted') {
        setError("Too many requests. Please wait a moment before trying again.");
      } else if (firebaseError.code === 'functions/failed-precondition') {
        setError("Chat service is not properly configured. Please contact support.");
      } else {
        setError(`Failed to get response: ${firebaseError.message || 'Unknown error'}`);
      }
    } finally {
      setFetching(false);
    }
  }, [prompt]);

  useEffect(() => {
    setTimeout(() => {
      ScrollComponent.reinitialization();
    }, 100);
  }, []);

  const [messages, setMessages] = useState<Array<IMessage>>(chatExamples);
  const [messageText, setMessageText] = useState<string>("");
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);

  const updateScroll = useCallback(() => {
    if (!scrollRef.current || !messagesRef.current) {
      return;
    }

    const ps: ScrollComponent = DataUtil.get(scrollRef.current, "ps");
    scrollRef.current.scrollTop = parseInt(
      getCSS(messagesRef.current, "height")
    );

    if (ps) {
      ps.update();
    }
  }, []);

  const addMessagesToList = useCallback((newMessages: Array<IMessage>) => {
    setMessages((prevMessages) => [...prevMessages, ...newMessages]);
    updateScroll();
  }, [updateScroll]);

  const sendMessage = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.keyCode !== 13) {
      return;
    }

    e.preventDefault();
    const messageId = `msg-${Date.now()}`;
    const newMessage: IMessage = {
      id: messageId,
      user: "Grace Logan",
      text: messageText,
      when: "Just now",
      avatarURL: toAbsoluteUrl("/media/svg/avatars/018-girl-9.svg"),
      isAuthor: true,
    };
    addMessagesToList([newMessage]);
    setMessageText("");
    setTimeout(() => {
      const autoReply: IMessage = {
        ...automaticAnswerMessage,
        id: `auto-${Date.now()}`,
      };
      addMessagesToList([autoReply]);
    }, 1000);
  }, [messageText, addMessagesToList]);

  return (
    <div className="d-flex flex-column flex-lg-row">


      {/* begin::Content*/}
      <div className="flex-lg-row-fluid" id="kt_chat_content">

      <div className="flex flex-col items-center justify-center p-4 w-[600px]">
        <div className="bg-white border rounded-lg p-6 w-full">
          <label htmlFor="prompt" className="block text-sm font-medium text-gray-700">Prompt</label>
            <input
                type="text"
                name="prompt"
                id="prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="mt-1 p-2 block w-full rounded-md border"
            />
            <button
            disabled={fetching || !prompt.trim()}
                onClick={handleSubmit}
                className={`${fetching || !prompt.trim() ? 'bg-gray-600 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50'} mt-4 px-4 py-2  text-white rounded-md `}
            >
                {fetching ? 'Fetching AI Response' : 'Submit Prompt'}
            </button>
            {error && (
              <div className="mt-2 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}
          </div>

          <div className="bg-white border shadow-lg rounded-lg p-6 w-full mt-8">
            <label className="block text-sm font-medium text-gray-700">Answer</label>
            <div className="mt-1 block w-full rounded-md border-gray-300 p-4">
                {output}
            </div>
          </div>
        </div>

        {/* begin::Card*/}
        <div className="card card-custom">
          {/* begin::Header*/}

          {/* end::Header*/}

          {/* begin::Body*/}
          <div className="card-body px-9">
            {/* begin::Scroll*/}
            <div
              ref={scrollRef}
              className="scroll-y me-lg-n6 pe-lg-5"
              data-kt-scroll="true"
              data-kt-scroll-height="{'default' : '400px', 'lg' : 'auto'}"
              data-kt-scroll-dependencies="#kt_header, #kt_toolbar, #kt_footer, #kt_chat_content_header, #kt_chat_content_footer"
              data-kt-scroll-wrappers="#kt_content, #kt_wrapper"
              data-kt-scroll-offset="{'default' : '10px', 'lg' : '52px'}"
            >
              {/* begin::Messages*/}
              <div className="messages" ref={messagesRef}>
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`d-flex flex-column mb-5 align-items-${
                      msg.isAuthor ? "end" : "start"
                    }`}
                  >
                    {msg.isAuthor && (
                      <>
                        {/* begin::Message Out*/}
                        <div className="d-flex align-items-center">
                          <div className="d-flex flex-column text-end">
                            <span className="text-gray-600 fw-bolder">
                              {msg.user}
                            </span>
                            <span className="text-muted fw-bold fs-7">
                              {msg.when}
                            </span>
                          </div>
                          {/* begin::Symbol*/}
                          <div className="symbol symbol-40px flex-shrink-0 ms-4">
                            <span className="symbol-label bg-light">
                              <img
                                src={msg.avatarURL}
                                className="h-75 align-self-end"
                                alt={`${msg.user}'s avatar`}
                              />
                            </span>
                          </div>
                          {/* end::Symbol*/}
                        </div>
                        <div className="rounded mt-2 p-5 bg-light-success text-gray-600 text-end mw-400px">
                          {msg.text}
                        </div>
                        {/* end::Message Out*/}
                      </>
                    )}
                    {!msg.isAuthor && (
                      <>
                        {/* begin::Message*/}

                        <div className="d-flex align-items-center">
                          {/* begin::Symbol*/}
                          <div className="symbol symbol-40px flex-shrink-0 me-4">
                            <span className="symbol-label bg-light">
                              <img
                                src={msg.avatarURL}
                                className="h-75 align-self-end"
                                alt={`${msg.user}'s avatar`}
                              />
                            </span>
                          </div>
                          {/* end::Symbol*/}
                          <div className="d-flex flex-column">
                            <span className="text-gray-600 fw-bolder">
                              {msg.user}
                            </span>
                            <span className="text-muted fw-bold fs-7">
                              {msg.when}
                            </span>
                          </div>
                        </div>
                        <div className="rounded mt-2 p-5 text-gray-600  mw-400px bg-light-primary text-start">
                          {msg.text}
                        </div>
                        {/* end::Message In*/}
                      </>
                    )}
                  </div>
                ))}
              </div>
              {/* end::Messages*/}
            </div>
            {/* end::Scroll*/}
          </div>
          {/* end::Body*/}

          {/* begin::Footer*/}
          <div
            className="card-footer align-items-center px-7 py-4"
            id="kt_chat_content_footer"
          >
            {/* begin::Compose*/}
            <div className="position-relative">
              <textarea
                className="form-control border-0 p-2 resize-none overflow-hidden"
                rows={1}
                placeholder="Reply..."
                onChange={(e) => setMessageText(e.target.value)}
                value={messageText}
                onKeyDown={sendMessage}
              />
              <div className="position-absolute top-0 end-0 mr-n2">
                <button
                  type="button"
                  className="btn btn-icon btn-sm btn-active-light-primary"
                  aria-label="Attach file"
                >
                  <i className="fas fa-paperclip fs-4" aria-hidden="true"></i>
                </button>
              </div>
            </div>
            {/* begin::Compose*/}
          </div>
          {/* end::Footer*/}
        </div>
        {/* end::Card*/}
      </div>
      {/* end::Content*/}
    </div>
  );
};

export { ChatPage };
